import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { isTouchDevice } from './../../utils/device';
import { useSelector } from 'react-redux';
import NetworkStrengthChecker from './networkFinal';
import {
  FaExclamationTriangle,
  FaMicrophone,
  FaMicrophoneSlash,
  FaSyncAlt,
  FaVideo,
  FaVideoSlash,
} from 'react-icons/fa';
import useVoiceActivity from './../../hooks/useVoiceActivity';
import { applyAudioOutputDevice } from './../../utils/meetingAudio';
import { getStoredMeetingSessionId } from './../../utils/meetingAuth';
import { shouldShowVideoReceiveFailure } from './../../utils/mediaReliability';

const AvatarMicRing = ({ level = 0, speaking = false }) => {
  if (!speaking) return null;

  const SIZE = 128;
  const STROKE = 6;
  const VISUAL_GAIN = 2.4;

  const pct = Math.max(0, Math.min(1, (level || 0) * VISUAL_GAIN));
  const cx = SIZE / 2;
  const cy = SIZE / 2;
  const R  = cx - STROKE - 2;
  const C  = 2 * Math.PI * R;
  const dash = Math.max(0.0001, C * pct);
  const stroke = '#F59E0B';

  return (
    <svg className="absolute inset-0 m-auto" width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} aria-hidden="true">
      <circle cx={cx} cy={cy} r={R} stroke={stroke} strokeWidth={STROKE} fill="none" opacity="0.35" />
      <circle cx={cx} cy={cy} r={R} stroke={stroke} strokeWidth={STROKE} fill="none" strokeLinecap="round" strokeDasharray={`${dash},999`} />
    </svg>
  );
};

const MicBars = ({ level = 0, muted }) => {
  if (muted) return null;
  const h1 = 6 + Math.round(level * 10);
  const h2 = 10 + Math.round(level * 16);
  const h3 = 6 + Math.round(level * 13);
  return (
    <div className="absolute top-2 right-2 flex items-end gap-[3px] z-10">
      <span style={{ height: h1 }} className="w-[3px] bg-white/90 rounded-sm transition-[height] duration-75" />
      <span style={{ height: h2 }} className="w-[3px] bg-white/90 rounded-sm transition-[height] duration-75" />
      <span style={{ height: h3 }} className="w-[3px] bg-white/90 rounded-sm transition-[height] duration-75" />
    </div>
  );
};

const getDefaultVol = () =>
  (typeof window !== 'undefined' && typeof window.__defaultOutputVolume === 'number') ? window.__defaultOutputVolume : 1.0;

const getPlaybackBoost = () =>
  (typeof window !== 'undefined' && typeof window.__playbackBoost === 'number') ? window.__playbackBoost : 1.0;

// Do not boost remote voices by default: louder speaker leakage makes acoustic
// echo harder for the capture device to cancel. User-selected speaker volume
// and the optional explicit boost remain available.
const DEFAULT_REMOTE_PLAYBACK_GAIN = 1;
let sharedPlaybackAudioContext = null;

const getSharedPlaybackAudioContext = () => {
  if (typeof window === 'undefined') return null;
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return null;
  if (!sharedPlaybackAudioContext || sharedPlaybackAudioContext.state === 'closed') {
    try {
      sharedPlaybackAudioContext = new AudioContextClass({
        latencyHint: 'interactive',
      });
    } catch {
      sharedPlaybackAudioContext = new AudioContextClass();
    }
  }
  return sharedPlaybackAudioContext;
};

const getProcessedPlaybackGain = () =>
  Math.max(
    0,
    Math.min(
      3,
      getDefaultVol() * getPlaybackBoost() * DEFAULT_REMOTE_PLAYBACK_GAIN
    )
  );

const VideoParticipant = ({
  userId,
  username,
  stream,
  screenStream,
  audioStream,
  isLocal,
  isVideoEnabled,
  isVideoStateKnown,
  isAudioEnabled,
  isAudioStateKnown,
  participant,
  isMobileScreenShare,
  networkStats,
  sessionId,
  mainVideoRef,
  onSpeakingChange,
  onRetryVideo,
  onRetryDelivery,
  onRetryAudioDelivery,
  onRemoteVideoHealthChange,
  deliveryReports,
  audioDeliveryReports,
  deliveryReceiverSessionIds,
}) => {
  const examRoom = useSelector(state => state.room.currentRoom);
  const examStudent = examRoom?.meetingKind === 'exam' && String(participant?.user?.role || participant?.role || '') === 'student';
  const videoRef = useRef(null);
  const screenRef = useRef(null);
  const audioRef = useRef(null);
  const boosterRef = useRef(null);

  const [isSpeaking, setIsSpeaking] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [remotePlaybackStatus, setRemotePlaybackStatus] = useState(
    isLocal ? 'local' : 'connecting'
  );
  const [retryingVideo, setRetryingVideo] = useState(false);
  const [audioOutputDeviceId, setAudioOutputDeviceId] = useState(() => {
    if (typeof window === 'undefined') return '';
    try {
      return String(localStorage.getItem('meeting.audioOutputId') || '');
    } catch {
      return '';
    }
  });
  const controlsTimeout = useRef();
  const playbackStatusRef = useRef(remotePlaybackStatus);
  const autoRetryAtRef = useRef(0);

  const userInitial = useMemo(() => username?.charAt(0)?.toUpperCase() || '?', [username]);

  /* ===== store/network stats ===== */
  const statsMap = useSelector((s) => s?.room?.participantNetworkStats || {});
  const localHandRaised = useSelector((s) => Boolean(s?.media?.handRaised));
  const mySessionId =
    sessionId ||
    (typeof window !== 'undefined'
      ? String(getStoredMeetingSessionId() || '')
      : '');

  const storeStats = (mySessionId && statsMap[String(mySessionId)]) || (sessionId && statsMap[String(sessionId)]) || statsMap?.[userId] || {};
  const myStats = (networkStats && Object.keys(networkStats).length ? networkStats : storeStats) || {};

  const kbpsVal = typeof myStats.bitrateKbps === 'number' ? myStats.bitrateKbps : (typeof myStats.bitrate === 'number' ? myStats.bitrate : (typeof myStats.bitrate === 'string' ? parseFloat(myStats.bitrate) : undefined));
  const bitrateKbps = (typeof kbpsVal === 'number' && !Number.isNaN(kbpsVal)) ? Math.round(kbpsVal) : undefined;

  const isScreenSharing = !!screenStream;
  const primaryStream = isScreenSharing ? screenStream : stream;
  const secondaryStream = (isScreenSharing && !isLocal) ? stream : null;
  const primaryVideoTrack = primaryStream?.getVideoTracks?.()?.[0] || null;

  const reportPlaybackStatus = useCallback((status, reason = '') => {
    if (isLocal) return;
    const normalized = String(status || 'connecting');
    if (playbackStatusRef.current === normalized) return;
    playbackStatusRef.current = normalized;
    setRemotePlaybackStatus(normalized);
    onRemoteVideoHealthChange?.({
      sessionId,
      userId,
      status: normalized,
      reason,
      observedAt: Date.now(),
    });
  }, [isLocal, onRemoteVideoHealthChange, sessionId, userId]);

  /* ===== VAD ===== */
  const vadStream = isLocal ? (audioStream || stream) : (audioStream || stream);
  const { level, isSpeaking: speakingNow } = useVoiceActivity(vadStream, {
    energyThreshold: 0.12,
    speakHold: 80,
    dropHold: 240,
  });
  
  useEffect(() => { setIsSpeaking(speakingNow); }, [speakingNow]);

  const lastEmitRef = useRef({ speaking: false, level: 0 });
  const rafRef = useRef(null);
  
  useEffect(() => {
    const prev = lastEmitRef.current;
    const changed = prev.speaking !== speakingNow || (typeof level === 'number' && Math.abs((prev.level ?? 0) - level) > 0.05);
    if (!changed) return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      onSpeakingChange?.({ sessionId, speaking: speakingNow, level });
      lastEmitRef.current = { speaking: speakingNow, level };
      rafRef.current = null;
    });
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [speakingNow, level, sessionId, onSpeakingChange]);

  useEffect(() => () => clearTimeout(controlsTimeout.current), []);
  const handleTouchStart = () => {
    if (!isTouchDevice()) return;
    setShowControls(true);
    clearTimeout(controlsTimeout.current);
    controlsTimeout.current = setTimeout(() => setShowControls(false), 3000);
  };

  useEffect(() => {
    const videoElement = videoRef.current;
    const screenElement = screenRef.current;
    return () => {
      if (videoElement) videoElement.srcObject = null;
      if (screenElement) screenElement.srcObject = null;
    };
  }, []);

  // Primary / secondary video tag sources
  useEffect(() => {
    if (videoRef.current && primaryStream) {
      if (videoRef.current.srcObject !== primaryStream) {
        videoRef.current.srcObject = primaryStream;
      }
      videoRef.current.play?.().catch(() => undefined);
    } else if (videoRef.current) {
      if (videoRef.current.srcObject) videoRef.current.srcObject = null;
    }
  }, [
    primaryStream,
    primaryVideoTrack?.id,
    isScreenSharing,
    isVideoEnabled,
  ]);

  // Receiver-side health is based on actual frame progression in this video
  // element. A live MediaStreamTrack alone is not enough: it may remain "live"
  // while a low-network consumer is frozen.
  useEffect(() => {
    if (isLocal || isScreenSharing) return undefined;
    if (!isVideoStateKnown) {
      reportPlaybackStatus(
        'camera-unknown',
        'Waiting for the participant camera status.'
      );
      return undefined;
    }
    if (!isVideoEnabled) {
      reportPlaybackStatus('camera-off', 'The participant turned the camera off.');
      return undefined;
    }

    const track = primaryVideoTrack;
    if (!track || track.readyState !== 'live') {
      reportPlaybackStatus('connecting', 'Waiting for a remote video track.');
      const unavailableTimer = window.setTimeout(() => {
        reportPlaybackStatus('unavailable', 'No remote video track was received.');
      }, 4500);
      return () => window.clearTimeout(unavailableTimer);
    }

    const element = videoRef.current;
    const supportsVideoFrameCallback =
      typeof element?.requestVideoFrameCallback === 'function';
    const supportsPlaybackQuality =
      typeof element?.getVideoPlaybackQuality === 'function';
    const useMediaTimeFallback =
      !supportsVideoFrameCallback && !supportsPlaybackQuality;
    let lastCurrentTime = Number(element?.currentTime || 0);
    let lastDecodedFrames = Number(
      element?.getVideoPlaybackQuality?.()?.totalVideoFrames || 0
    );
    let lastFrameAt = Date.now();
    let frameCallbackId = null;
    let cancelled = false;
    reportPlaybackStatus('connecting', 'Waiting for the first decoded frame.');

    const markReceiving = () => {
      lastFrameAt = Date.now();
      reportPlaybackStatus('receiving', 'Remote video frames are arriving.');
    };
    const markWaiting = () => {
      if (Date.now() - lastFrameAt > 7000) {
        reportPlaybackStatus('stalled', 'Remote video frames stopped advancing.');
      }
    };
    const markUnavailable = () =>
      reportPlaybackStatus('unavailable', 'The remote video track ended.');
    const watchDecodedFrame = (_timestamp, metadata = {}) => {
      if (cancelled) return;
      if (Number.isFinite(metadata.mediaTime)) {
        lastCurrentTime = Math.max(lastCurrentTime, metadata.mediaTime);
      }
      markReceiving();
      frameCallbackId = element?.requestVideoFrameCallback?.(watchDecodedFrame);
    };

    element?.addEventListener('playing', markReceiving);
    if (useMediaTimeFallback) {
      element?.addEventListener('timeupdate', markReceiving);
    }
    element?.addEventListener('waiting', markWaiting);
    element?.addEventListener('stalled', markWaiting);
    track.addEventListener?.('ended', markUnavailable);
    track.addEventListener?.('mute', markWaiting);
    track.addEventListener?.('unmute', markReceiving);
    if (supportsVideoFrameCallback) {
      frameCallbackId = element.requestVideoFrameCallback(watchDecodedFrame);
    }

    const timer = window.setInterval(() => {
      if (track.readyState !== 'live') {
        markUnavailable();
        return;
      }
      const currentTime = Number(element?.currentTime || 0);
      const decodedFrames = supportsPlaybackQuality
        ? Number(element.getVideoPlaybackQuality().totalVideoFrames || 0)
        : lastDecodedFrames;
      if (supportsPlaybackQuality && decodedFrames > lastDecodedFrames) {
        lastDecodedFrames = decodedFrames;
        lastCurrentTime = Math.max(lastCurrentTime, currentTime);
        markReceiving();
      } else if (
        useMediaTimeFallback &&
        currentTime > lastCurrentTime + 0.01
      ) {
        lastCurrentTime = currentTime;
        markReceiving();
      } else if (Date.now() - lastFrameAt > 7000) {
        markWaiting();
      }
    }, 1500);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      if (
        frameCallbackId != null &&
        typeof element?.cancelVideoFrameCallback === 'function'
      ) {
        element.cancelVideoFrameCallback(frameCallbackId);
      }
      element?.removeEventListener('playing', markReceiving);
      if (useMediaTimeFallback) {
        element?.removeEventListener('timeupdate', markReceiving);
      }
      element?.removeEventListener('waiting', markWaiting);
      element?.removeEventListener('stalled', markWaiting);
      track.removeEventListener?.('ended', markUnavailable);
      track.removeEventListener?.('mute', markWaiting);
      track.removeEventListener?.('unmute', markReceiving);
    };
  }, [
    isLocal,
    isScreenSharing,
    isVideoEnabled,
    isVideoStateKnown,
    primaryVideoTrack,
    reportPlaybackStatus,
    retryingVideo,
  ]);

  useEffect(() => {
    if (screenRef.current && secondaryStream) {
      if (screenRef.current.srcObject !== secondaryStream) {
        screenRef.current.srcObject = secondaryStream;
      }
      screenRef.current.play?.().catch(() => undefined);
    } else if (screenRef.current) {
      if (screenRef.current.srcObject) screenRef.current.srcObject = null;
    }
  }, [secondaryStream]);

  /* ===============================
     CLEAN AUDIO PLAYBACK (NO DOUBLE AUDIO)
     =============================== */
  const remoteAudioSource = useMemo(() => {
    if (isLocal) return null;
    if (audioStream) return audioStream;
    const tracks = stream?.getAudioTracks?.() || [];
    if (!tracks.length) return null;
    const s = new MediaStream();
    tracks.forEach(t => s.addTrack(t));
    return s;
  }, [isLocal, audioStream, stream]);

  // Native playback is the single default audio path. Web Audio is used only
  // for an explicit >100% speaker boost; the native element is muted before
  // that path is connected so the same remote track is never heard twice.
  useEffect(() => {
    if (!remoteAudioSource || isLocal) return undefined;
    const el = audioRef.current;
    if (!el) return undefined;

    el.srcObject = remoteAudioSource;
    el.muted = false;
    el.volume = Math.max(0, Math.min(1, getDefaultVol() * getPlaybackBoost()));

    let cancelled = false;
    let activating = false;
    const activateProcessedPlayback = async () => {
      if (cancelled) return false;
      if (boosterRef.current) return true;
      if (activating) return false;
      activating = true;
      try {
        const context = getSharedPlaybackAudioContext();
        if (!context) return false;
        if (audioOutputDeviceId) {
          if (typeof context.setSinkId !== 'function') return false;
          await context.setSinkId(audioOutputDeviceId);
        } else if (typeof context.setSinkId === 'function') {
          await context.setSinkId('');
        }
        await context.resume?.();
        if (cancelled || context.state !== 'running') return false;

        const source = context.createMediaStreamSource(remoteAudioSource);
        const gain = context.createGain();
        const limiter = context.createDynamicsCompressor();
        gain.gain.value = getProcessedPlaybackGain();
        limiter.threshold.value = -6;
        limiter.knee.value = 3;
        limiter.ratio.value = 12;
        limiter.attack.value = 0.003;
        limiter.release.value = 0.25;

        el.muted = true;
        el.volume = 0;
        source.connect(gain);
        gain.connect(limiter);
        limiter.connect(context.destination);
        boosterRef.current = {
          context,
          gain,
          limiter,
          source,
          stream: remoteAudioSource,
        };
        return true;
      } catch (error) {
        console.warn('Processed meeting audio is unavailable; using native playback:', error);
        el.muted = false;
        el.volume = Math.max(0, Math.min(1, getDefaultVol() * getPlaybackBoost()));
        return false;
      } finally {
        activating = false;
      }
    };

    const resumePlayback = async () => {
      try {
        await applyAudioOutputDevice(el, audioOutputDeviceId);
      } catch (error) {
        console.warn('Selected speaker unavailable; using system default:', error);
      }
      const wantsBoost = getProcessedPlaybackGain() > 1.001;
      const processed = wantsBoost
        ? await activateProcessedPlayback()
        : false;
      if (!processed) {
        el.muted = false;
        el.volume = Math.max(
          0,
          Math.min(1, getDefaultVol() * getPlaybackBoost())
        );
        el.play?.().catch((error) =>
          console.warn('Audio play is waiting for a user gesture:', error)
        );
      }
    };

    void resumePlayback();
    // Mobile browsers may reject autoplay even after the WebAudio context was
    // unlocked elsewhere. Keep this media element recoverable on the next
    // pointer/key gesture instead of forcing the participant to rejoin.
    window.addEventListener('pointerdown', resumePlayback, { passive: true });
    window.addEventListener('touchstart', resumePlayback, { passive: true });
    window.addEventListener('keydown', resumePlayback);
    
    return () => {
      cancelled = true;
      const booster = boosterRef.current;
      if (booster?.stream === remoteAudioSource) {
        try { booster.source?.disconnect(); } catch {}
        try { booster.gain?.disconnect(); } catch {}
        try { booster.limiter?.disconnect(); } catch {}
        boosterRef.current = null;
      }
      if (el.srcObject === remoteAudioSource) {
        el.pause?.();
        el.srcObject = null;
        el.muted = false;
      }
      window.removeEventListener('pointerdown', resumePlayback);
      window.removeEventListener('touchstart', resumePlayback);
      window.removeEventListener('keydown', resumePlayback);
    };
  }, [audioOutputDeviceId, remoteAudioSource, isLocal]);

  useEffect(() => {
    if (isLocal) return undefined;
    const applyOutputDevice = async (event) => {
      const requested = String(
        event?.detail?.deviceId ?? localStorage.getItem('meeting.audioOutputId') ?? ''
      );
      setAudioOutputDeviceId(requested);
      const element = audioRef.current;
      try {
        await applyAudioOutputDevice(element, requested);
      } catch (error) {
        console.warn('Speaker selection failed:', error);
      }
    };
    void applyOutputDevice();
    window.addEventListener('meeting-output-device-change', applyOutputDevice);
    return () => window.removeEventListener('meeting-output-device-change', applyOutputDevice);
  }, [isLocal]);

  // Handle Volume Setting Changes dynamically
  useEffect(() => {
    const applyVolume = () => {
      if (audioRef.current && !isLocal) {
        const base = getDefaultVol();
        const boost = getPlaybackBoost();
        if (boosterRef.current?.gain) {
          const { context, gain } = boosterRef.current;
          gain.gain.setTargetAtTime(
            getProcessedPlaybackGain(),
            context.currentTime,
            0.02
          );
        } else {
          audioRef.current.volume = Math.max(0, Math.min(1, base * boost));
        }
      }
    };
    window.addEventListener('audio-settings-change', applyVolume);
    return () => window.removeEventListener('audio-settings-change', applyVolume);
  }, [isLocal]);

  const showPrimaryVideo = useMemo(() => {
    const hasTrack = primaryVideoTrack?.readyState === 'live';
    const primaryEnabled = isScreenSharing ? true : isVideoEnabled;
    return primaryEnabled && hasTrack;
  }, [primaryVideoTrack, isVideoEnabled, isScreenSharing]);
  const statsVideoStatus = String(myStats?.media?.video?.status || '');
  const effectiveVideoStatus =
    !isLocal &&
    isVideoEnabled &&
    ['stalled', 'unavailable'].includes(statsVideoStatus) &&
    remotePlaybackStatus !== 'receiving'
      ? statsVideoStatus
      : remotePlaybackStatus;
  const videoNeedsRetry = shouldShowVideoReceiveFailure({
    isLocal,
    isScreenSharing,
    mediaStateKnown: isVideoStateKnown,
    mediaEnabled: isVideoEnabled,
    status: effectiveVideoStatus,
  });

  const retryVideo = useCallback(async (automatic = false) => {
    if (!onRetryVideo || retryingVideo) return;
    setRetryingVideo(true);
    reportPlaybackStatus('recovering', automatic
      ? 'Automatic video recovery started.'
      : 'Video recovery requested.');
    try {
      await onRetryVideo(sessionId, { automatic });
    } finally {
      window.setTimeout(() => setRetryingVideo(false), 1200);
    }
  }, [
    onRetryVideo,
    reportPlaybackStatus,
    retryingVideo,
    sessionId,
  ]);

  // Give transient packet loss a short grace period, then recover this
  // participant's consumer automatically. The retry is throttled per tile.
  useEffect(() => {
    if (!videoNeedsRetry || !onRetryVideo) return undefined;
    const now = Date.now();
    const wait = Math.max(1800, 10000 - (now - autoRetryAtRef.current));
    const timer = window.setTimeout(() => {
      autoRetryAtRef.current = Date.now();
      void retryVideo(true);
    }, wait);
    return () => window.clearTimeout(timer);
  }, [onRetryVideo, retryVideo, videoNeedsRetry]);

  const recentDeliveryReports = useMemo(() => {
    if (!isLocal) return [];
    const expectedReceivers = new Set(
      (deliveryReceiverSessionIds || []).map((value) => String(value))
    );
    return Object.values(deliveryReports || {})
      .filter((report) => {
        const reporterSessionId = String(report?.reporterSessionId || '');
        return (
          expectedReceivers.has(reporterSessionId) &&
          Date.now() - Number(report?.observedAt || 0) < 20000
        );
      });
  }, [deliveryReceiverSessionIds, deliveryReports, isLocal]);
  const deliverySummary = useMemo(() => {
    if (!isLocal || !isVideoEnabled) return null;
    const expectedCount = new Set(
      (deliveryReceiverSessionIds || []).map((value) => String(value))
    ).size;
    if (expectedCount === 0) {
      return { label: 'Video ready', tone: 'success', retryReports: [] };
    }

    const retryReports = recentDeliveryReports.filter((report) =>
      ['stalled', 'unavailable'].includes(
        String(report?.status || '').toLowerCase()
      )
    );
    if (retryReports.length) {
      return {
        label: 'Not visible · Refresh',
        tone: 'warning',
        retryReports,
      };
    }
    if (
      recentDeliveryReports.some((report) =>
        ['connecting', 'recovering'].includes(
          String(report?.status || '').toLowerCase()
        )
      )
    ) {
      return { label: 'Refreshing…', tone: 'checking', retryReports: [] };
    }
    const receivingCount = recentDeliveryReports.filter(
      (report) => String(report?.status || '').toLowerCase() === 'receiving'
    ).length;
    if (receivingCount >= expectedCount) {
      return { label: 'Visible to all', tone: 'success', retryReports: [] };
    }
    return { label: 'Checking video…', tone: 'checking', retryReports: [] };
  }, [
    deliveryReceiverSessionIds,
    isLocal,
    isVideoEnabled,
    recentDeliveryReports,
  ]);
  const audioDeliverySummary = useMemo(() => {
    if (!isLocal || !isAudioEnabled) return null;
    const expectedReceivers = new Set(
      (deliveryReceiverSessionIds || []).map((value) => String(value))
    );
    const expectedCount = expectedReceivers.size;
    if (expectedCount === 0) {
      return { label: 'Audio ready', tone: 'success', retryReports: [] };
    }

    const recentReports = Object.values(audioDeliveryReports || {}).filter(
      (report) =>
        expectedReceivers.has(String(report?.reporterSessionId || '')) &&
        Date.now() - Number(report?.observedAt || 0) < 20000
    );
    const retryReports = recentReports.filter((report) =>
      ['stalled', 'unavailable'].includes(
        String(report?.status || '').toLowerCase()
      )
    );
    if (retryReports.length) {
      return {
        label: 'Audio unavailable · Repair',
        tone: 'warning',
        retryReports,
      };
    }
    if (
      recentReports.some((report) =>
        ['connecting', 'recovering'].includes(
          String(report?.status || '').toLowerCase()
        )
      )
    ) {
      return { label: 'Checking audio…', tone: 'checking', retryReports: [] };
    }
    const receivingCount = recentReports.filter(
      (report) => String(report?.status || '').toLowerCase() === 'receiving'
    ).length;
    if (receivingCount >= expectedCount) {
      return {
        label: 'Audio reaching all',
        tone: 'success',
        retryReports: [],
      };
    }
    return { label: 'Checking audio…', tone: 'checking', retryReports: [] };
  }, [
    audioDeliveryReports,
    deliveryReceiverSessionIds,
    isAudioEnabled,
    isLocal,
  ]);
  const handRaised = isLocal
    ? localHandRaised
    : Boolean(participant?.isRaised);
  const isReconnecting =
    !isLocal && participant?.disconnected === true;

  return (
    <div
      className={`relative w-full h-full overflow-hidden flex items-center justify-center rounded-2xl shadow-lg border border-gray-700 ${isSpeaking ? 'ring-4 ring-emerald-500/90 shadow-emerald-500/30 transition-all duration-200' : 'ring-0'} ${isScreenSharing ? 'bg-black' : 'bg-gray-800'}`}
      onTouchStart={handleTouchStart}
    >
      {handRaised && (
        <div className="absolute left-2 top-2 z-30 flex max-w-[85%] items-center gap-1.5 rounded-full bg-amber-400 px-2.5 py-1.5 text-xs font-semibold text-gray-950 shadow-lg sm:text-sm">
          <span aria-hidden className="text-base leading-none sm:text-lg">✋</span>
          <span className="truncate">Hand raised</span>
        </div>
      )}
      {isReconnecting && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-slate-950/70 backdrop-blur-[1px]">
          <div className="rounded-xl border border-amber-300/30 bg-slate-900/90 px-4 py-3 text-center shadow-xl">
            <FaSyncAlt className="mx-auto mb-2 animate-spin text-amber-300" />
            <p className="text-xs font-semibold text-white">Reconnecting…</p>
            <p className="mt-1 text-[10px] text-slate-300">
              Waiting briefly for this participant to return.
            </p>
          </div>
        </div>
      )}
      {/* Camera off → avatar */}
      {!showPrimaryVideo ? (
        <div className="w-full h-full relative flex items-center justify-center">
          <div aria-hidden className="absolute inset-0" style={{ background: 'radial-gradient(1200px 600px at 20% 10%, rgba(99,102,241,0.25) 0%, rgba(0,0,0,0) 60%), radial-gradient(900px 500px at 80% 80%, rgba(16,185,129,0.18) 0%, rgba(0,0,0,0) 55%), linear-gradient(180deg, #0b0f19 0%, #0a0e18 60%, #0a0d15 100%)' }} />
          <div aria-hidden className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
            <AvatarMicRing level={level} speaking={isAudioEnabled && level > 0.05} />
          </div>
          <div className="relative w-24 h-24 md:w-28 md:h-28 z-10" style={{ filter: isSpeaking ? `drop-shadow(0 0 ${6 + Math.round(level * 10)}px rgba(245,158,11,0.65))` : 'none', transition: 'filter 120ms linear' }}>
            <div className="absolute inset-0 rounded-full flex items-center justify-center text-white text-3xl select-none bg-indigo-600 transition-transform duration-150" style={{ transform: isSpeaking ? 'scale(1.06)' : 'scale(1.0)' }}>
              {userInitial}
            </div>
          </div>
        </div>
      ) : (
        <>
          <MicBars level={level} muted={!isAudioEnabled} />
          <video
            ref={(el) => { videoRef.current = el; if (isLocal && mainVideoRef) mainVideoRef.current = el; }}
            autoPlay playsInline muted
            className={['w-full h-full', (examStudent || isScreenSharing) ? 'object-contain' : (isLocal ? 'object-cover' : (isMobileScreenShare ? 'object-contain' : 'object-cover'))].join(' ')}
          />
        </>
      )}

      {videoNeedsRetry && (
        <div className="absolute inset-x-3 top-1/2 z-30 -translate-y-1/2 rounded-xl border border-amber-300/30 bg-slate-950/90 p-3 text-center shadow-xl backdrop-blur">
          <FaExclamationTriangle className="mx-auto mb-1.5 text-amber-300" />
          <p className="text-xs font-semibold text-white">
            {effectiveVideoStatus === 'stalled'
              ? 'Video has stalled'
              : 'Video not received'}
          </p>
          <p className="mt-1 text-[10px] text-slate-300">
            Automatic recovery is running for this participant only.
          </p>
          <button
            type="button"
            disabled={retryingVideo}
            onClick={(event) => {
              event.stopPropagation();
              void retryVideo(false);
            }}
            className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-sky-500 px-3 py-1.5 text-[11px] font-semibold text-slate-950 hover:bg-sky-400 disabled:opacity-60"
          >
            <FaSyncAlt className={retryingVideo ? 'animate-spin' : ''} />
            {retryingVideo ? 'Recovering…' : 'Retry video'}
          </button>
        </div>
      )}

      {isTouchDevice() && showControls && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
          <div className="bg-gray-800 p-4 rounded-lg">
            <p className="text-white text-center mb-2">{username} {isScreenSharing && '(Sharing Screen)'}</p>
            {isAudioStateKnown && !isAudioEnabled && (
              <div className="text-red-400 text-sm text-center">Muted</div>
            )}
          </div>
        </div>
      )}

      {/* Hidden audio element for remote audio playback */}
      <audio
        ref={audioRef}
        playsInline
        data-meeting-remote-audio={isLocal ? undefined : sessionId}
        style={{ display: 'none' }}
      />

      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-3 py-2 rounded-b-2xl">
        <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-white">
            {username} {isLocal && '(You)'} {isScreenSharing && ' (Screen)'}
          </span>
          <div className="flex max-w-full flex-wrap items-center justify-end gap-1 sm:max-w-[76%]">
            {deliverySummary && (
              deliverySummary.tone === 'warning' ? (
                <button
                  type="button"
                  className="inline-flex shrink-0 items-center gap-1 rounded-full border border-red-400/40 bg-red-500/20 px-1.5 py-1 text-[9px] font-semibold text-red-200 hover:bg-red-500/30 sm:text-[10px]"
                  onClick={(event) => {
                    event.stopPropagation();
                    deliverySummary.retryReports.forEach((report) => {
                      onRetryDelivery?.(report.reporterSessionId);
                    });
                  }}
                  title="Some viewers cannot see your video. Refresh video delivery."
                  aria-label="Your video is not visible to everyone. Refresh video delivery."
                >
                  <FaSyncAlt aria-hidden />
                  {deliverySummary.label}
                </button>
              ) : (
                <span
                  className={`inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-1 text-[9px] font-semibold sm:text-[10px] ${
                    deliverySummary.tone === 'success'
                      ? 'bg-emerald-500/20 text-emerald-200'
                      : 'bg-sky-500/20 text-sky-200'
                  }`}
                  title={deliverySummary.label}
                  aria-live="polite"
                >
                  <span
                    aria-hidden
                    className={`h-1.5 w-1.5 rounded-full ${
                      deliverySummary.tone === 'success'
                        ? 'bg-emerald-400'
                        : 'bg-sky-400'
                    }`}
                  />
                  {deliverySummary.label}
                </span>
              )
            )}
            {audioDeliverySummary && (
              audioDeliverySummary.tone === 'warning' ? (
                <button
                  type="button"
                  className="inline-flex shrink-0 items-center gap-1 rounded-full border border-amber-300/40 bg-amber-500/20 px-1.5 py-1 text-[9px] font-semibold text-amber-100 hover:bg-amber-500/30 sm:text-[10px]"
                  onClick={(event) => {
                    event.stopPropagation();
                    audioDeliverySummary.retryReports.forEach((report) => {
                      onRetryAudioDelivery?.(report.reporterSessionId);
                    });
                  }}
                  title="A receiver is not getting your microphone audio. Repair that audio consumer."
                  aria-label="Your audio is unavailable to one or more participants. Repair audio delivery."
                >
                  <FaSyncAlt aria-hidden />
                  {audioDeliverySummary.label}
                </button>
              ) : (
                <span
                  className={`inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-1 text-[9px] font-semibold sm:text-[10px] ${
                    audioDeliverySummary.tone === 'success'
                      ? 'bg-emerald-500/20 text-emerald-200'
                      : 'bg-sky-500/20 text-sky-200'
                  }`}
                  title="Audio delivery is confirmed by receiver WebRTC statistics."
                  aria-live="polite"
                >
                  <span
                    aria-hidden
                    className={`h-1.5 w-1.5 rounded-full ${
                      audioDeliverySummary.tone === 'success'
                        ? 'bg-emerald-400'
                        : 'bg-sky-400'
                    }`}
                  />
                  {audioDeliverySummary.label}
                </span>
              )
            )}
            <div className="flex items-center gap-1" title="Network">
              <NetworkStrengthChecker
                metrics={{
                  ...myStats,
                  bitrateKbps: Number.isFinite(bitrateKbps)
                    ? bitrateKbps
                    : myStats.bitrateKbps,
                }}
              />
            </div>
            <span
              className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-[9px] font-semibold sm:text-[10px] ${
                !isAudioStateKnown
                  ? 'bg-slate-500/40 text-slate-100'
                  : isAudioEnabled
                    ? 'bg-emerald-500/25 text-emerald-100'
                    : 'bg-red-500/90 text-white'
              }`}
              title={
                !isAudioStateKnown
                  ? 'Microphone status syncing'
                  : isAudioEnabled
                    ? 'Microphone on'
                    : 'Microphone muted'
              }
              aria-label={
                !isAudioStateKnown
                  ? `${username} microphone status syncing`
                  : `${username} ${isAudioEnabled ? 'microphone on' : 'muted'}`
              }
            >
              {!isAudioStateKnown ? (
                <FaSyncAlt aria-hidden className="animate-spin" />
              ) : isAudioEnabled ? (
                <FaMicrophone aria-hidden />
              ) : (
                <FaMicrophoneSlash aria-hidden />
              )}
              {!isAudioStateKnown ? 'Mic syncing' : isAudioEnabled ? 'Mic on' : 'Muted'}
            </span>
            <span
              className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-[9px] font-semibold sm:text-[10px] ${
                !isVideoStateKnown
                  ? 'bg-slate-500/40 text-slate-100'
                  : isVideoEnabled
                    ? 'bg-emerald-500/25 text-emerald-100'
                    : 'bg-red-500/90 text-white'
              }`}
              title={
                !isVideoStateKnown
                  ? 'Camera status syncing'
                  : isVideoEnabled
                    ? 'Camera on'
                    : 'Camera off'
              }
              aria-label={
                !isVideoStateKnown
                  ? `${username} camera status syncing`
                  : `${username} camera ${isVideoEnabled ? 'on' : 'off'}`
              }
            >
              {!isVideoStateKnown ? (
                <FaSyncAlt aria-hidden className="animate-spin" />
              ) : isVideoEnabled ? (
                <FaVideo aria-hidden />
              ) : (
                <FaVideoSlash aria-hidden />
              )}
              {!isVideoStateKnown
                ? 'Camera syncing'
                : isVideoEnabled
                  ? 'Camera on'
                  : 'Camera off'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

VideoParticipant.propTypes = {
  userId: PropTypes.string.isRequired,
  username: PropTypes.string.isRequired,
  stream: typeof MediaStream !== 'undefined' ? PropTypes.instanceOf(MediaStream) : PropTypes.any,
  screenStream: typeof MediaStream !== 'undefined' ? PropTypes.instanceOf(MediaStream) : PropTypes.any,
  audioStream: typeof MediaStream !== 'undefined' ? PropTypes.instanceOf(MediaStream) : PropTypes.any,
  isLocal: PropTypes.bool,
  isVideoEnabled: PropTypes.bool,
  isVideoStateKnown: PropTypes.bool,
  isAudioEnabled: PropTypes.bool,
  isAudioStateKnown: PropTypes.bool,
  participant: PropTypes.object,
  isMobileScreenShare: PropTypes.bool,
  networkStats: PropTypes.any,
  sessionId: PropTypes.string,
  mainVideoRef: PropTypes.shape({ current: PropTypes.any }),
  onSpeakingChange: PropTypes.func,
  onRetryVideo: PropTypes.func,
  onRetryDelivery: PropTypes.func,
  onRetryAudioDelivery: PropTypes.func,
  onRemoteVideoHealthChange: PropTypes.func,
  deliveryReports: PropTypes.object,
  audioDeliveryReports: PropTypes.object,
  deliveryReceiverSessionIds: PropTypes.arrayOf(PropTypes.string),
};

VideoParticipant.defaultProps = {
  isLocal: false,
  isVideoEnabled: false,
  isVideoStateKnown: false,
  isAudioEnabled: false,
  isAudioStateKnown: false,
  participant: null,
  isMobileScreenShare: false,
  networkStats: null,
  sessionId: null,
  onSpeakingChange: () => {},
  onRetryVideo: null,
  onRetryDelivery: null,
  onRetryAudioDelivery: null,
  onRemoteVideoHealthChange: null,
  deliveryReports: null,
  audioDeliveryReports: null,
  deliveryReceiverSessionIds: [],
};

export default VideoParticipant;
